/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { mcpApprovalRefreshEvidence } from "../funcs/mcpApprovalRefreshEvidence.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ApprovalRequestDetail } from "../models/components/approvalrequestdetail.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RefreshMcpApprovalEvidenceRequest,
  RefreshMcpApprovalEvidenceSecurity,
} from "../models/operations/refreshmcpapprovalevidence.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RefreshMcpApprovalEvidenceMutationVariables = {
  request: RefreshMcpApprovalEvidenceRequest;
  security?: RefreshMcpApprovalEvidenceSecurity | undefined;
  options?: RequestOptions;
};

export type RefreshMcpApprovalEvidenceMutationData = ApprovalRequestDetail;

export type RefreshMcpApprovalEvidenceMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * refreshEvidence mcpApproval
 *
 * @remarks
 * Re-run every evidence source for a request and replace its current evidence with the fresh gather. Frozen decision snapshots are never touched.
 */
export function useRefreshMcpApprovalEvidenceMutation(
  options?: MutationHookOptions<
    RefreshMcpApprovalEvidenceMutationData,
    RefreshMcpApprovalEvidenceMutationError,
    RefreshMcpApprovalEvidenceMutationVariables
  >,
): UseMutationResult<
  RefreshMcpApprovalEvidenceMutationData,
  RefreshMcpApprovalEvidenceMutationError,
  RefreshMcpApprovalEvidenceMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRefreshMcpApprovalEvidenceMutation(client, options),
    ...options,
  });
}

export function mutationKeyRefreshMcpApprovalEvidence(): MutationKey {
  return ["@gram/client", "mcpApproval", "refreshEvidence"];
}

export function buildRefreshMcpApprovalEvidenceMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RefreshMcpApprovalEvidenceMutationVariables,
  ) => Promise<RefreshMcpApprovalEvidenceMutationData>;
} {
  return {
    mutationKey: mutationKeyRefreshMcpApprovalEvidence(),
    mutationFn: function refreshMcpApprovalEvidenceMutationFn({
      request,
      security,
      options,
    }): Promise<RefreshMcpApprovalEvidenceMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(mcpApprovalRefreshEvidence(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
